import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import Icon from './Icon.jsx'
import { describeError } from '../lib/api.js'

/**
 * Transient feedback, top-right, in the macOS notification idiom: a translucent card that
 * announces itself and then gets out of the way.
 *
 * The point is that it does not move the page. The banners this replaces were laid out in
 * the document, so a save error shoved the whole board down a line and every cell someone
 * was aiming at moved — which, on a screen being filled at speed, is worse than the error.
 *
 * Only for things that HAPPENED. A standing condition — "you have read-only access here",
 * "this row is cut" — stays on the page, because a message that disappears cannot carry a
 * fact that is still true.
 */

const ToastContext = createContext(null)

const KIND = {
  success: { icon: 'checkCircle', ms: 3500 },
  info: { icon: 'info', ms: 4500 },
  warn: { icon: 'alert', ms: 7000 },
  // Errors sit until dismissed: whatever the person was mid-way through did not happen, and
  // a four-second window to read why is a good way to not read why.
  error: { icon: 'alert', ms: 0 },
}

let nextId = 1

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([])
  const [paused, setPaused] = useState(false)

  const dismiss = useCallback((id) => {
    // Mark it leaving so the exit animation can run, then drop it.
    setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)))
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 220)
  }, [])

  const push = useCallback((kind, input, opts = {}) => {
    // Anything thrown by api.* can be handed straight to toast.error — no call site needs to
    // remember to unwrap it, and none of them can accidentally drop the server's `details`
    // by reaching for `.message`. String(err) would have rendered "Error: ..." at best.
    const message = typeof input === 'string' ? input : describeError(input, opts.fallback)
    if (!message) return null
    const id = nextId++
    setToasts((list) => [
      // A burst of failures should not become a column down the screen; keep the last few.
      ...list.slice(-3),
      // `action` is { label, onClick }: one button under the message, for the toast that
      // offers to take something back. Pressing it dismisses the toast as well.
      { id, kind, message, title: opts.title, action: opts.action, ms: opts.duration ?? KIND[kind].ms },
    ])
    return id
  }, [])

  const api = useMemo(
    () => ({
      success: (m, o) => push('success', m, o),
      info: (m, o) => push('info', m, o),
      warn: (m, o) => push('warn', m, o),
      error: (m, o) => push('error', m, o),
      dismiss,
    }),
    [push, dismiss],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div
        className="toast-stack"
        role="region"
        aria-label="Notifications"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} paused={paused} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

function ToastItem({ toast, paused, onDismiss }) {
  const { id, kind, message, title, action, ms, leaving } = toast
  // Time already served, so hovering pauses the countdown rather than restarting it — a
  // toast you moved the mouse onto to read should not get a fresh four seconds each time.
  const spent = useRef(0)
  const startedAt = useRef(Date.now())

  useEffect(() => {
    if (!ms || leaving) return undefined
    if (paused) {
      spent.current += Date.now() - startedAt.current
      return undefined
    }
    startedAt.current = Date.now()
    const remaining = Math.max(400, ms - spent.current)
    const timer = setTimeout(() => onDismiss(id), remaining)
    return () => clearTimeout(timer)
  }, [id, ms, paused, leaving, onDismiss])

  return (
    <div
      className={`toast toast-${kind} ${leaving ? 'leaving' : ''}`}
      role={kind === 'error' ? 'alert' : 'status'}
    >
      <Icon name={KIND[kind].icon} size={18} className="toast-icon" />
      <div className="toast-body">
        {title && <div className="toast-title">{title}</div>}
        <div className="toast-message">{message}</div>
        {action && (
          <button
            type="button"
            className="toast-action"
            onClick={() => {
              onDismiss(id)
              action.onClick()
            }}
          >
            {action.label}
          </button>
        )}
      </div>
      <button className="toast-close" onClick={() => onDismiss(id)} aria-label="Dismiss">
        <Icon name="close" size={14} strokeWidth={2.2} />
      </button>
    </div>
  )
}

/**
 * How long an Undo stays on offer. Longer than a plain success, because the person has to
 * notice, read, and decide — and shorter than an error, because a board that has moved on
 * for half a minute is no longer the board the undo would land in.
 */
export const UNDO_MS = 8000

/**
 * A toast that offers to take a destructive action back.
 *
 * `restore` re-creates what was removed — the caller knows the row it just deleted, and the
 * server's create endpoint is the honest way to put it back: it re-runs every rule the
 * original booking met and writes its own audit event, so an undo reads in the log as what it
 * is. Deleting and clearing were the two clicks on the board with no way back short of asking
 * an owner to read the audit log, which is not a way back.
 *
 * `onRestored` is what the caller refetches afterwards. Failures — the cell was filled in the
 * meantime, the person was disabled — come back as an ordinary error toast.
 */
export function useUndo(onRestored) {
  const toast = useToast()
  return useCallback(
    (message, restore) =>
      toast.success(message, {
        duration: UNDO_MS,
        action: {
          label: 'Undo',
          onClick: async () => {
            try {
              await restore()
              onRestored?.()
              toast.success('Put back.')
            } catch (err) {
              toast.error(err)
            }
          },
        },
      }),
    [toast, onRestored],
  )
}

/**
 * Returns { success, info, warn, error, dismiss }. Safe to call outside a provider — it
 * falls back to the console rather than throwing, so a stray call can never be what takes
 * the board down.
 */
export function useToast() {
  const ctx = useContext(ToastContext)
  return (
    ctx || {
      success: (m) => console.info(m),
      info: (m) => console.info(m),
      warn: (m) => console.warn(m),
      error: (m) => console.error(m),
      dismiss: () => {},
    }
  )
}
